import { ShieldCheck, Star, RefreshCw, Lock, ShoppingBag, Flame, Sparkles } from 'lucide-react';
import { sanitize } from '@/lib/sanitize';

const HeroBanner = ({ totalOffers, siteSettings }) => {
  // Novos textos padrão atualizados
  const defaultTitle = '🛍️ Bem-vindo ao Carvalho Achados';
  const defaultSubtitle = 'Aqui você encontra os melhores achadinhos da internet, selecionados com base em qualidade, avaliações reais e custo-benefício. Trabalhamos com plataformas confiáveis para trazer ofertas atualizadas todos os dias. 👉 Clique no produto e finalize sua compra com total segurança diretamente no site oficial.';
  
  const heroTitle = sanitize(siteSettings?.hero_title) || defaultTitle;
  const heroSubtitle = sanitize(siteSettings?.hero_subtitle) || defaultSubtitle;
  const gradStart = siteSettings?.hero_gradient_start || '#FF6B00';
  const gradEnd = siteSettings?.hero_gradient_end || '#FFD700';

  // Frases de confiança
  const trustBadges = [
    { icon: Star, text: 'Produtos com alta avaliação' },
    { icon: ShieldCheck, text: 'Ofertas selecionadas com cuidado' },
    { icon: RefreshCw, text: 'Atualizado diariamente' },
    { icon: Lock, text: 'Compra segura nas plataformas oficiais' },
  ];

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)` }}
      data-testid="hero-banner"
    >
      {/* Sparkle particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Dot texture */}
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="sparkle-particle"
            style={{
              left: `${(i * 8.3) % 100}%`,
              top: `${(i * 13.7) % 80 + 10}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-10 md:py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black mb-4 leading-tight text-white drop-shadow-md">
            {heroTitle}
          </h1>
          <p className="text-white/90 text-sm md:text-base mb-6 max-w-3xl mx-auto font-medium leading-relaxed">
            {heroSubtitle}
          </p>

          {/* Frases de Confiança - Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-6 max-w-3xl mx-auto">
            {trustBadges.map((badge, index) => (
              <div 
                key={index}
                className="flex items-center justify-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-2 md:px-3 md:py-2.5 rounded-lg text-white/95"
              >
                <badge.icon className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 text-green-300" />
                <span className="font-medium text-[10px] md:text-xs leading-tight">{badge.text}</span>
              </div>
            ))}
          </div>

          {/* BOTÕES SHOPEE, TEMU E SHEIN - LADO A LADO */}
          <div className="flex flex-col items-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 flex-wrap">
              {/* Botão Shopee */}
              <a
                href="https://collshp.com/carvalhocomercio?view=storefront"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 px-6 py-3.5 md:px-8 md:py-4 rounded-full font-black text-sm md:text-base text-white transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                data-testid="hero-shopee-link"
              >
                {/* Fundo com gradiente animado - Verde Shopee */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 animate-gradient-x" />
                
                {/* Brilho pulsante */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 blur-lg opacity-60 group-hover:opacity-80 animate-pulse" />
                
                {/* Shimmer effect */}
                <span className="absolute inset-0 rounded-full overflow-hidden">
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </span>
                
                {/* Conteúdo do botão */}
                <span className="relative flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 md:w-5 md:h-5 animate-bounce" />
                  <span className="drop-shadow-lg">Vitrine Shopee</span>
                  <Flame className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-300 animate-pulse" />
                </span>
              </a>

              {/* Botão Temu */}
              <a
                href="https://temu.to/k/gssc2z9fkh6"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 px-6 py-3.5 md:px-8 md:py-4 rounded-full font-black text-sm md:text-base text-white transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                data-testid="hero-temu-link"
              >
                {/* Fundo com gradiente animado - Laranja Temu */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FB6D28] via-[#FF8C42] to-[#FB6D28] animate-gradient-x" />
                
                {/* Brilho pulsante */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FB6D28] to-[#FF9F5A] blur-lg opacity-60 group-hover:opacity-80 animate-pulse" />
                
                {/* Shimmer effect */}
                <span className="absolute inset-0 rounded-full overflow-hidden">
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </span>
                
                {/* Conteúdo do botão */}
                <span className="relative flex items-center gap-2">
                  <Star className="w-4 h-4 md:w-5 md:h-5 animate-bounce" />
                  <span className="drop-shadow-lg">Ofertas Temu</span>
                  <Flame className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-300 animate-pulse" />
                </span>
              </a>

              {/* Botão Shein */}
              <a
                href="https://onelink.shein.com/3/5mkmuv1s1ug2"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 px-6 py-3.5 md:px-8 md:py-4 rounded-full font-black text-sm md:text-base text-white transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                data-testid="hero-shein-link"
              >
                {/* Fundo com gradiente animado - Preto Shein */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-gray-900 via-black to-gray-800 animate-gradient-x" />
                
                {/* Brilho pulsante */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-gray-700 to-gray-900 blur-lg opacity-60 group-hover:opacity-80 animate-pulse" />
                
                {/* Shimmer effect */}
                <span className="absolute inset-0 rounded-full overflow-hidden">
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </span>
                
                {/* Conteúdo do botão */}
                <span className="relative flex items-center gap-2">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-bounce" />
                  <span className="drop-shadow-lg">Moda Shein</span>
                  <Flame className="w-3.5 h-3.5 md:w-4 md:h-4 text-pink-300 animate-pulse" />
                </span>
              </a>
            </div>
            
            {/* Texto de urgência abaixo dos botões */}
            <div className="mt-4 flex items-center gap-2 animate-pulse">
              <span className="text-white/90 text-xs md:text-sm font-semibold bg-red-600/80 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-lg">
                🔥 Ofertas por tempo limitado! Aproveite antes que acabe!
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60L1440 60L1440 20C1440 20 1200 0 720 20C240 40 0 20 0 20L0 60Z" fill="#F5F5F5" />
        </svg>
      </div>
    </section>
  );
};

export default HeroBanner;
